import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, ArrowUpRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Homepage tile that surfaces the most recent post that has multiple images
 * — hero image + 3-thumb strip on the right, click-through to the post.
 * Sits below the news mosaic to give the page a magazine-like closing beat.
 */
const LatestGallery = () => {
  const [post, setPost] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Pick the freshest post with 3+ images in its body. This one genuinely
        // needs the bodies, so it asks for them; every other listing does not.
        const r = await fetch(`${API}/posts?limit=25&include=content`);
        const d = await r.json();
        const list = Array.isArray(d) ? d : (d.items || []);
        const picked = list.find((p) => {
          const c = p.content || '';
          const imgs = (c.match(/<img[^>]+src="([^"]+)"/g) || []);
          return imgs.length >= 3;
        });
        if (!alive || !picked) return;
        const srcs = [...(picked.content || '').matchAll(/<img[^>]+src="([^"]+)"/g)].map((m) => m[1]);
        setPost({
          ...picked,
          hero: picked.featured_image || srcs[0],
          thumbs: srcs.slice(picked.featured_image ? 0 : 1, picked.featured_image ? 3 : 4),
        });
      } catch { /* empty */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!post) return null;

  return (
    <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-testid="home-latest-gallery">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-cyan-400 mb-1 inline-flex items-center gap-1.5">
              <Camera size={11}/> In pictures
            </p>
            <h2 className="text-2xl md:text-3xl font-bold" data-testid="home-latest-gallery-h2">
              The latest gallery
            </h2>
          </div>
          <Link
            to={`/news/${post.slug}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300 hover:text-white transition-colors"
            data-testid="home-latest-gallery-cta"
          >
            View story <ArrowUpRight size={13}/>
          </Link>
        </div>

        <Link
          to={`/news/${post.slug}`}
          className="group grid md:grid-cols-2 gap-3 md:gap-4 rounded-2xl overflow-hidden ring-1 ring-white/10"
        >
          {/* Hero */}
          <div className="relative aspect-[4/3] md:aspect-auto bg-black/40 overflow-hidden">
            {post.hero && (
              <img src={post.hero} alt={post.title} loading="lazy"
                   className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                   onError={(e) => { e.target.style.opacity = '0.2'; }}/>
            )}
            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
              <h3 className="text-white text-lg md:text-xl font-bold leading-snug line-clamp-2">
                {post.title}
              </h3>
              {post.excerpt && (
                <p className="text-white/80 text-sm mt-1 line-clamp-1">{post.excerpt}</p>
              )}
            </div>
          </div>

          {/* Thumb strip */}
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            {post.thumbs.map((src, i) => (
              <div key={i} className="relative aspect-[4/5] bg-black/40 rounded-lg overflow-hidden">
                <img src={src} alt="" loading="lazy"
                     className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                     onError={(e) => { e.target.style.opacity = '0.2'; }}/>
              </div>
            ))}
          </div>
        </Link>
      </div>
    </section>
  );
};

export default LatestGallery;
